// src/admin/AdminSavedCarts.jsx
// Carts (with artwork) that customers left before paying, so the team can follow up directly.
import { useEffect, useState } from "react";
import { apiRequest } from "../services/apiClient";

const STATUSES = [
  { key: "open", label: "New", color: "#f5c200" },
  { key: "contacted", label: "Contacted", color: "#00aadd" },
  { key: "converted", label: "Ordered", color: "#4ade80" },
  { key: "dismissed", label: "Dismissed", color: "#9ca3af" },
];

function money(value) {
  return "$" + Number(value || 0).toFixed(2);
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString("en-CA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
}

export default function AdminSavedCarts() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open");
  const [expanded, setExpanded] = useState(null);
  const [notes, setNotes] = useState({});

  async function load() {
    try {
      const data = await apiRequest("/admin/saved-carts");
      setCarts(data);
      setNotes(Object.fromEntries(data.map(cart => [cart.id, cart.adminNotes])));
      setError("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function update(cart, patch) {
    try {
      await apiRequest(`/admin/saved-carts/${cart.id}`, { method: "PATCH", body: JSON.stringify(patch) });
      setCarts(prev => prev.map(item => item.id === cart.id ? { ...item, ...patch } : item));
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(cart) {
    if (!window.confirm(`Delete the saved cart from ${cart.customerEmail}?`)) return;
    try {
      await apiRequest(`/admin/saved-carts/${cart.id}`, { method: "DELETE" });
      setCarts(prev => prev.filter(item => item.id !== cart.id));
    } catch (err) {
      setError(err.message);
    }
  }

  const visible = carts.filter(cart => filter === "all" || cart.status === filter);
  const count = key => carts.filter(cart => cart.status === key).length;

  if (loading) return <div style={{ padding: 60, color: "rgba(255,255,255,0.5)" }}>Loading saved carts...</div>;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: "#fff", margin: 0 }}>Saved Carts</h2>
        <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>
          Customers who entered their details and/or uploaded artwork but haven't paid yet. Contact them directly to finish the order.
        </p>
      </div>
      {error && <p style={{ color: "#f87171", fontSize: 13 }}>{error}</p>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {[{ key: "all", label: "All" }, ...STATUSES].map(status => (
          <button key={status.key} className={"adm-btn" + (filter === status.key ? " adm-btn-primary" : "")} onClick={() => setFilter(status.key)}>
            {status.label} ({status.key === "all" ? carts.length : count(status.key)})
          </button>
        ))}
      </div>

      <div className="adm-card">
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>Customer</th><th>Items</th><th>Artwork</th><th>Subtotal</th><th>Last activity</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {visible.map(cart => {
                const withArtwork = cart.items.filter(item => item.artworkUrl).length;
                const open = expanded === cart.id;
                return [
                  <tr key={cart.id}>
                    <td>
                      <strong>{cart.customerName || "—"}</strong><br />
                      <a href={`mailto:${cart.customerEmail}`} style={{ color: "#00aadd" }}>{cart.customerEmail}</a>
                      {cart.customerPhone && <><br /><a href={`tel:${cart.customerPhone}`} style={{ color: "#00aadd" }}>{cart.customerPhone}</a></>}
                    </td>
                    <td>{cart.items.length}</td>
                    <td style={{ color: withArtwork === cart.items.length ? "#4ade80" : "#f5c200" }}>{withArtwork} of {cart.items.length}</td>
                    <td>{money(cart.subtotalCents / 100)}</td>
                    <td>{formatDate(cart.updatedAt)}</td>
                    <td>
                      <select value={cart.status} onChange={e => update(cart, { status: e.target.value })}
                        style={{ background: "rgba(255,255,255,0.06)", color: STATUSES.find(s => s.key === cart.status)?.color, border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "6px 8px" }}>
                        {STATUSES.map(status => <option key={status.key} value={status.key} style={{ color: "#000" }}>{status.label}</option>)}
                      </select>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="adm-btn" onClick={() => setExpanded(open ? null : cart.id)}>{open ? "Hide" : "View"}</button>{" "}
                      <button className="adm-btn adm-btn-danger" onClick={() => remove(cart)}>Delete</button>
                    </td>
                  </tr>,
                  open && (
                    <tr key={cart.id + "-details"}>
                      <td colSpan="7" style={{ background: "rgba(255,255,255,0.03)" }}>
                        <div style={{ padding: 12, display: "grid", gap: 14, color: "rgba(255,255,255,0.8)", fontSize: 13 }}>
                          <div>
                            <strong>Address:</strong>{" "}
                            {[cart.shippingInfo.address, cart.shippingInfo.address2, cart.shippingInfo.city, cart.shippingInfo.state, cart.shippingInfo.postcode, cart.shippingInfo.country].filter(Boolean).join(", ") || "—"}
                            {cart.couponCode && <> &nbsp;·&nbsp; <strong>Coupon:</strong> {cart.couponCode}</>}
                          </div>
                          {cart.items.map((item, index) => (
                            <div key={index} style={{ border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: 10 }}>
                              <strong>{item.productName}</strong> — {money(item.displayPrice)}
                              <div style={{ color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
                                {Object.entries(item.optionLabels || {}).map(([key, value]) => `${key}: ${value}`).join(" | ")}
                              </div>
                              <div style={{ marginTop: 6 }}>
                                {item.artworkUrl
                                  ? <a href={item.artworkUrl} target="_blank" rel="noreferrer" style={{ color: "#4ade80" }}>📎 {item.artworkName || "Download artwork"}</a>
                                  : <span style={{ color: "#f5c200" }}>No artwork uploaded yet</span>}
                              </div>
                            </div>
                          ))}
                          <div>
                            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>Follow-up notes</label>
                            <textarea rows={3} value={notes[cart.id] || ""} onChange={e => setNotes(prev => ({ ...prev, [cart.id]: e.target.value }))}
                              style={{ width: "100%", background: "rgba(255,255,255,0.06)", color: "#fff", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: 10, fontFamily: "inherit" }} />
                            <button className="adm-btn adm-btn-primary" style={{ marginTop: 8 }} onClick={() => update(cart, { adminNotes: notes[cart.id] || "" })}>Save notes</button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
              {!visible.length && <tr><td colSpan="7" style={{ color: "rgba(255,255,255,0.45)" }}>No saved carts here.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
